// Vanderbilt University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Vanderbilt's Common Data Set 2025-2026 (CDS_2025-2026.xlsx), linked
//    from the Common Data Sets page of Vanderbilt's public data site: C1
//    applicants, admits and enrollees for Fall 2025 with the residency table
//    (its "unknown" column is 0), C8 test policy, C21/C22 early plans, G1/G5
//    2026-2027 costs. The spreadsheet's C21 date cells show #VALUE!, so the
//    dates come from the admissions page; the C21 counts are filled.
//  - admissions.vanderbilt.edu: Apply (decision plans and the dates and
//    deadlines for fall 2027 entry, with 2026 and 2027 dates), Application
//    Process: First-year (Blair, Peabody and engineering notes), Testing
//    Policies (test-optional for fall 2027 and 2028 entry, scores required
//    for fall 2029 entry), and Find Your Major (the majors file).
//  - blair.vanderbilt.edu: Prescreening & Auditions.
//  - College Scorecard, school id 221999 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1 first-year figures and G5 resident figures are used.
// G5 lists transportation as "varies", so there is no transportation row and
// the total is the sum of the rows shown. The financial aid office's
// 2026-27 cost page (vanderbilt.edu/financialaid/costs-undergraduate/)
// lists the same tuition, housing, food, fee and books figures, a $1,998 personal allowance (the CDS says $2,000) and
// transportation as "Varies".
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Vanderbilt Common Data Set 2025-2026',
        url: 'https://www.vanderbilt.edu/data/public-data/common-data-sets/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Vanderbilt Undergraduate Admissions, Apply: decision plans and dates and deadlines',
        url: 'https://admissions.vanderbilt.edu/apply/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Vanderbilt Undergraduate Admissions, Application Process: First-year',
        url: 'https://admissions.vanderbilt.edu/apply/first-year-process/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Vanderbilt Undergraduate Admissions, Testing Policies',
        url: 'https://admissions.vanderbilt.edu/apply/testing-policies/',
        read: '2026-10-01',
    },
    blair: {
        label: 'Blair School of Music, Prescreening & Auditions',
        url: 'https://blair.vanderbilt.edu/admissions/prescreening-auditions/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?221999-Vanderbilt-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'vanderbilt-university',
    kbId: 'vanderbilt_university',
    name: 'Vanderbilt University',
    shortName: 'Vanderbilt',
    aliases: ['Vandy'],
    location: 'Nashville, TN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Vanderbilt brand colors); decoration only.
    branding: {
        primary: '#CFAE70',
        secondary: '#1C1C1C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/vanderbilt_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 48196,
        admitted: 2593,
        enrolled: 1635,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2885, admitted: 301 },
            { group: 'Out-of-state', applied: 36130, admitted: 1926 },
            { group: 'International', applied: 9181, admitted: 366 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Vanderbilt\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set gives one early decision count for fall 2025, not split into Early Decision I and II. Vanderbilt has no early action plan.',
        rows: [
            { plan: 'Early Decision I and II', applied: 6202, admitted: 874 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Vanderbilt\'s five undergraduate schools. Some ask for more:',
        rows: [
            { school: 'Blair School of Music', requirement: 'Complete the Blair School of Music Application in your MyAppVU portal after you submit the Common App, Coalition or QuestBridge application, with one artistic recommendation, and a prescreening video for your instrument, voice, composition or jazz. The Blair application is due November 1 for Early Decision I, with a December 1 priority deadline for Early Decision II and Regular Decision.', source: 'firstYear' },
            { school: 'Blair School of Music (auditions)', requirement: 'Prescreening requirements are the same for every major within an instrument or voice (jazz has its own); the audition follows if you are approved.', source: 'blair' },
            { school: 'Peabody College', requirement: 'Applicants choosing Elementary Education, Secondary Education or Special Education answer an extra short question about why they want to be an educator.', source: 'firstYear' },
            { school: 'School of Engineering', requirement: 'Vanderbilt highly recommends that engineering applicants have taken calculus, calculus-based physics and chemistry.', source: 'firstYear' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1, 2027' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1, 2026', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 1, 2027', decision: 'Mid-February 2027', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1, 2027', decision: 'Late March 2027', binding: false },
        ],
        note: 'Matriculation deposits are due December 31, 2026 for Early Decision I, March 1, 2027 for Early Decision II and May 1, 2027 for Regular Decision. Merit scholarship applications are due December 1, 2026. You can change your decision plan in the MyAppVU portal up to two weeks after that plan\'s deadline.',
        source: 'apply',
        binding: 'Early Decision I and II are binding. You, a parent or guardian and your school counselor sign the Early Decision Agreement, and if admitted you must withdraw all other applications right away.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Vanderbilt is test-optional for fall 2027 and fall 2028 entry and will require the SAT or ACT for fall 2029 entry. On the application you choose to be read with or without scores, and you cannot change that choice after you submit; the choice itself neither helps nor hurts. Vanderbilt superscores both the SAT and the ACT, and you may enter your scores on the application yourself; students who enroll then send official score reports.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 69822 },
            { item: 'Required fees', amount: 3384 },
            { item: 'Food and housing (on campus)', amount: 23690 },
            { item: 'Books and supplies', amount: 1100 },
            { item: 'Other expenses', amount: 2000 },
        ],
        total: 99996,
        note: 'For a first-year student living on campus. Vanderbilt lists transportation as varying, with no figure, so it is not in the total.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 15497,
            byIncome: [
                { income: '$0 to $30,000', amount: 3637 },
                { income: '$30,001 to $48,000', amount: 1908 },
                { income: '$48,001 to $75,000', amount: 5651 },
                { income: '$75,001 to $110,000', amount: 11284 },
                { income: 'Over $110,000', amount: 42583 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Vanderbilt\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
